import { useEffect } from 'react';

const SITE_URL = 'https://foxet.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image-v2.jpg`;

export interface PageMeta {
  title: string;
  description: string;
  /** route path, e.g. '/pre-sales'. Use '/' for the homepage. */
  path: string;
  ogImage?: string;
  /** 404 and other pages that shouldn't be indexed or carry a canonical */
  noindex?: boolean;
}

/** Sets meta[attr=key]'s content, returning a restore fn that puts back whatever was there before. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  const prevContent = existing?.getAttribute('content') ?? null;
  const existed = !!existing;
  const el = existing ?? document.head.appendChild(document.createElement('meta'));
  if (!existed) el.setAttribute(attr, key);
  el.setAttribute('content', content);

  return () => {
    if (!existed) {
      el.remove();
    } else if (prevContent !== null) {
      el.setAttribute('content', prevContent);
    }
  };
}

/**
 * Sets document title, description/OG/Twitter meta, canonical link, and robots for the
 * current route, and restores whatever was there before on unmount — so a page that
 * forgets to call this (or unmounts without a replacement route mounting first) doesn't
 * leave stale meta (e.g. a 404's noindex/removed-canonical) stuck for the rest of the SPA
 * session. Every route should still call this itself; the restore is a safety net, not a
 * substitute.
 */
export function usePageMeta({ title, description, path, ogImage = DEFAULT_OG_IMAGE, noindex = false }: PageMeta) {
  useEffect(() => {
    const url = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
    const prevTitle = document.title;
    document.title = title;

    const restoreFns = [
      upsertMeta('name', 'description', description),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:image', ogImage),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', ogImage),
      upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow'),
    ];

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const prevCanonicalHref = existingCanonical?.getAttribute('href') ?? null;
    const canonicalExisted = !!existingCanonical;

    if (noindex) {
      existingCanonical?.remove();
    } else {
      const link = existingCanonical ?? document.head.appendChild(document.createElement('link'));
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
    }

    return () => {
      document.title = prevTitle;
      restoreFns.forEach((restore) => restore());

      const currentCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!canonicalExisted) {
        currentCanonical?.remove();
      } else if (prevCanonicalHref !== null) {
        const link = currentCanonical ?? document.head.appendChild(document.createElement('link'));
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', prevCanonicalHref);
      }
    };
  }, [title, description, path, ogImage, noindex]);
}
